import { useMemo } from 'react';
import { toast } from 'sonner';

type NotificationType = 'info' | 'error' | 'success';

/** Only what callers actually reach for. `duration` is in ms; sonner's default is 4000. */
type NotificationOptions = { duration?: number };

/**
 * The app's one way to raise a transient message.
 *
 * Backed by `sonner` (mounted as `<Toaster>` in App.tsx). It replaced a hand-rolled toast
 * that kept its message in the global reducer: a single slot, so a second message replaced
 * the first, and the dismissal timeout was a `setTimeout` id carried around in app state and
 * cleared by the reducer. Stacking, timing and exit animation are the toaster's job now, so
 * none of that state exists any more.
 *
 * The returned callbacks are stable, so callers can list them in effect dependencies.
 */
export function useNotifications() {
  return useMemo(() => {
    const notify = (message: string, type: NotificationType = 'info', options?: NotificationOptions) => {
      if (!message) return;
      toast[type](message, options);
    };

    return {
      notify,
      notifyError: (message: string) => notify(message, 'error'),
      notifyInfo: (message: string) => notify(message, 'info'),
      notifySuccess: (message: string) => notify(message, 'success'),
    };
  }, []);
}
